// src/utils/audioStore.ts
import { create } from 'zustand';

interface AudioCache {
  [text: string]: string; // text -> audioSrc (base64 data URL)
}

interface PlaybackState {
  activeId: string | null;
  isPlaying: boolean;
  isPaused: boolean;
}

interface AudioState {
  audioCache: AudioCache;
  audioMap: { [key: string]: string };
  currentAudio: HTMLAudioElement | null;
  playbackState: PlaybackState;
  setPlaybackState: (state: Partial<PlaybackState>) => void;
  getAudioSrc: (
    text: string,
    fetchFunction: (text: string) => Promise<string>
  ) => Promise<string>;
  getAudioByKey: (key: string) => string | undefined;
  setAudioByKey: (key: string, value: string) => void;
  clearAudioCache: () => void;
  setCurrentAudio: (audio: HTMLAudioElement | null) => void;
  stopAudio: () => void;
}

export const useAudioStore = create<AudioState>((set, get) => ({
  audioCache: {},
  audioMap: {},
  currentAudio: null,
  playbackState: { activeId: null, isPlaying: false, isPaused: false },
  setPlaybackState: (newState) => set((state) => ({
    playbackState: { ...state.playbackState, ...newState }
  })),
  getAudioSrc: async (text, fetchFunction) => {
    const cachedSrc = get().audioCache[text];
    if (cachedSrc) {
      console.log('Serving audio from Zustand cache for:', text);
      return cachedSrc;
    }
    console.log('Fetching audio from API for:', text);
    const newAudioSrc = await fetchFunction(text);
    set((state) => ({
      audioCache: { ...state.audioCache, [text]: newAudioSrc },
    }));
    return newAudioSrc;
  },
  getAudioByKey: (key) => get().audioMap[key],
  setAudioByKey: (key, value) => {
    set((state) => ({
      audioMap: { ...state.audioMap, [key]: value }
    }));
  },
  clearAudioCache: () => set({ audioCache: {}, audioMap: {} }),
  setCurrentAudio: (audio) => set({ currentAudio: audio }),
  stopAudio: () => {
    const { currentAudio } = get();
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
      set({ currentAudio: null, playbackState: { activeId: null, isPlaying: false, isPaused: false } });
    } else {
      set({ playbackState: { activeId: null, isPlaying: false, isPaused: false } });
    }
  },
}));

// Clear any existing bloated shoonit-audio key from localStorage to free up space
try {
  if (typeof window !== 'undefined' && window.localStorage) {
    window.localStorage.removeItem('shoonit-audio');
  }
} catch (e) {
  // Ignore
}

export default useAudioStore;